import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Plus, Trash2, Pencil, BookOpen, ArrowUpRight, ArrowDownRight, Filter, X, Banknote, CreditCard, Wallet } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';
import { fmtDate } from '@/lib/dateFmt';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const CONTI = [
  { value: 'cassa', label: 'Cassa', icon: Wallet, color: 'text-amber-700 bg-amber-50 border-amber-200' },
  { value: 'banca', label: 'Banca', icon: Banknote, color: 'text-blue-700 bg-blue-50 border-blue-200' },
  { value: 'carta', label: 'Carta', icon: CreditCard, color: 'text-purple-700 bg-purple-50 border-purple-200' },
  { value: 'altro', label: 'Altro', icon: Wallet, color: 'text-slate-600 bg-slate-100 border-slate-200' },
];

const CATEGORIE = ['Incassi clienti', 'Pagamenti fornitori', 'Stipendi', 'Tasse e contributi', 'Carburante', 'Spese generali', 'Affitto', 'Materiali', 'Trasferte', 'Altro'];

const EMPTY = {
  data: new Date().toISOString().split('T')[0],
  descrizione: '',
  importo: '',
  tipo: 'uscita',
  conto: 'cassa',
  categoria: '',
  cantiere_id: '',
  note: '',
};

const fmt = (n) => `€ ${(Number(n) || 0).toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function PrimaNotaPage() {
  const [movimenti, setMovimenti] = useState([]);
  const [riepilogo, setRiepilogo] = useState({ entrate: 0, uscite: 0, saldo: 0, per_conto: {} });
  const [cantieri, setCantieri] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY);
  // Filters
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [filterDataDa, setFilterDataDa] = useState('');
  const [filterDataA, setFilterDataA] = useState('');
  const [filterTipo, setFilterTipo] = useState('all');
  const [filterConto, setFilterConto] = useState('all');
  const [filterCantiere, setFilterCantiere] = useState('all');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterDataDa) params.data_da = filterDataDa;
      if (filterDataA) params.data_a = filterDataA;
      if (filterTipo !== 'all') params.tipo = filterTipo;
      if (filterConto !== 'all') params.conto = filterConto;
      if (filterCantiere !== 'all') params.cantiere_id = filterCantiere;

      const [movRes, rieRes, cantRes] = await Promise.all([
        axios.get(`${API}/prima-nota`, { params, withCredentials: true }),
        axios.get(`${API}/prima-nota/riepilogo`, { params: { data_da: params.data_da, data_a: params.data_a }, withCredentials: true }),
        axios.get(`${API}/cantieri`, { withCredentials: true }),
      ]);
      setMovimenti(movRes.data || []);
      setRiepilogo(rieRes.data || { entrate: 0, uscite: 0, saldo: 0, per_conto: {} });
      setCantieri(cantRes.data || []);
    } catch {
      toast.error('Errore nel caricamento');
    } finally {
      setLoading(false);
    }
  }, [filterDataDa, filterDataA, filterTipo, filterConto, filterCantiere]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openNew = (tipo = 'uscita') => {
    setEditingId(null);
    setForm({ ...EMPTY, tipo });
    setDialogOpen(true);
  };

  const openEdit = (m) => {
    setEditingId(m.id);
    setForm({
      data: m.data,
      descrizione: m.descrizione || '',
      importo: m.importo || '',
      tipo: m.tipo,
      conto: m.conto || 'cassa',
      categoria: m.categoria || '',
      cantiere_id: m.cantiere_id || '',
      note: m.note || '',
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.descrizione.trim()) { toast.error('Descrizione obbligatoria'); return; }
    const imp = parseFloat(form.importo);
    if (!imp || imp <= 0) { toast.error('Importo deve essere positivo'); return; }
    try {
      const payload = { ...form, importo: imp };
      if (editingId) {
        await axios.put(`${API}/prima-nota/${editingId}`, payload, { withCredentials: true });
        toast.success('Movimento aggiornato');
      } else {
        await axios.post(`${API}/prima-nota`, payload, { withCredentials: true });
        toast.success('Movimento registrato');
      }
      setDialogOpen(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore');
    }
  };

  const handleDelete = async (m) => {
    if (!window.confirm(`Eliminare il movimento "${m.descrizione}" di ${fmt(m.importo)}?`)) return;
    try {
      await axios.delete(`${API}/prima-nota/${m.id}`, { withCredentials: true });
      toast.success('Movimento eliminato');
      fetchData();
    } catch { toast.error('Errore'); }
  };

  const clearFilters = () => {
    setFilterDataDa(''); setFilterDataA(''); setFilterTipo('all'); setFilterConto('all'); setFilterCantiere('all');
  };

  const activeFiltersCount = [filterDataDa, filterDataA, filterTipo !== 'all' ? 1 : 0, filterConto !== 'all' ? 1 : 0, filterCantiere !== 'all' ? 1 : 0].filter(Boolean).length;

  const contoMeta = (c) => CONTI.find(x => x.value === c) || CONTI[3];

  if (loading && movimenti.length === 0) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div></div>;
  }

  return (
    <div className="space-y-4 sm:space-y-6" data-testid="prima-nota-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold tracking-tight text-slate-900 flex items-center gap-2">
            <BookOpen className="h-5 w-5" />Prima Nota
          </h1>
          <p className="text-xs sm:text-sm text-slate-500 mt-1">Registro cassa/banca: tutti i movimenti finanziari giornalieri</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => openNew('entrata')} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="add-entrata-btn">
            <ArrowUpRight className="h-4 w-4 mr-1.5" />Entrata
          </Button>
          <Button onClick={() => openNew('uscita')} className="bg-red-600 hover:bg-red-700 text-white" data-testid="add-uscita-btn">
            <ArrowDownRight className="h-4 w-4 mr-1.5" />Uscita
          </Button>
        </div>
      </div>

      {/* Riepilogo */}
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
        <Card className="border-emerald-200 bg-emerald-50/40">
          <CardContent className="p-4">
            <div className="flex items-center gap-1.5"><ArrowUpRight className="h-4 w-4 text-emerald-600" /><p className="text-xs text-emerald-700 uppercase font-medium">Entrate</p></div>
            <p className="text-2xl font-bold text-emerald-700 mt-1">{fmt(riepilogo.entrate)}</p>
          </CardContent>
        </Card>
        <Card className="border-red-200 bg-red-50/40">
          <CardContent className="p-4">
            <div className="flex items-center gap-1.5"><ArrowDownRight className="h-4 w-4 text-red-600" /><p className="text-xs text-red-700 uppercase font-medium">Uscite</p></div>
            <p className="text-2xl font-bold text-red-700 mt-1">{fmt(riepilogo.uscite)}</p>
          </CardContent>
        </Card>
        <Card className={`col-span-2 lg:col-span-1 ${riepilogo.saldo >= 0 ? 'border-blue-200 bg-blue-50/40' : 'border-amber-200 bg-amber-50/40'}`}>
          <CardContent className="p-4">
            <div className="flex items-center gap-1.5"><Wallet className={`h-4 w-4 ${riepilogo.saldo >= 0 ? 'text-blue-600' : 'text-amber-600'}`} /><p className={`text-xs uppercase font-medium ${riepilogo.saldo >= 0 ? 'text-blue-700' : 'text-amber-700'}`}>Saldo</p></div>
            <p className={`text-2xl font-bold mt-1 ${riepilogo.saldo >= 0 ? 'text-blue-700' : 'text-amber-700'}`}>{fmt(riepilogo.saldo)}</p>
          </CardContent>
        </Card>
      </div>

      {/* Per conto */}
      {Object.keys(riepilogo.per_conto || {}).length > 0 && (
        <Card className="border-slate-200">
          <CardContent className="p-3">
            <p className="text-xs uppercase text-slate-500 font-medium mb-2">Per conto</p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {Object.entries(riepilogo.per_conto).map(([conto, totals]) => {
                const meta = contoMeta(conto);
                const Icon = meta.icon;
                return (
                  <div key={conto} className={`rounded border px-3 py-2 ${meta.color}`}>
                    <div className="flex items-center gap-1.5 mb-1"><Icon className="h-3.5 w-3.5" /><span className="text-xs font-medium capitalize">{meta.label}</span></div>
                    <p className={`text-sm font-bold ${totals.saldo >= 0 ? '' : 'text-red-700'}`}>{fmt(totals.saldo)}</p>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filters */}
      <Card className="border-slate-200">
        <CardContent className="p-3 space-y-3">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <Button variant="ghost" size="sm" onClick={() => setFiltersOpen(o => !o)} className="text-slate-700" data-testid="toggle-pn-filters">
              <Filter className="h-4 w-4 mr-1.5" />Filtri
              {activeFiltersCount > 0 && <Badge className="ml-2 bg-blue-600 text-white">{activeFiltersCount}</Badge>}
            </Button>
            {activeFiltersCount > 0 && (
              <Button variant="ghost" size="sm" onClick={clearFilters} className="text-slate-500" data-testid="clear-pn-filters">
                <X className="h-3.5 w-3.5 mr-1" />Reset
              </Button>
            )}
          </div>
          {filtersOpen && (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 pt-2 border-t border-slate-100">
              <div className="space-y-1"><Label className="text-xs text-slate-500">Da</Label><Input type="date" value={filterDataDa} onChange={e => setFilterDataDa(e.target.value)} /></div>
              <div className="space-y-1"><Label className="text-xs text-slate-500">A</Label><Input type="date" value={filterDataA} onChange={e => setFilterDataA(e.target.value)} /></div>
              <div className="space-y-1">
                <Label className="text-xs text-slate-500">Tipo</Label>
                <Select value={filterTipo} onValueChange={setFilterTipo}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tutti</SelectItem>
                    <SelectItem value="entrata">Solo entrate</SelectItem>
                    <SelectItem value="uscita">Solo uscite</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-slate-500">Conto</Label>
                <Select value={filterConto} onValueChange={setFilterConto}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tutti</SelectItem>
                    {CONTI.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-slate-500">Cantiere</Label>
                <Select value={filterCantiere} onValueChange={setFilterCantiere}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tutti</SelectItem>
                    {cantieri.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Tabella */}
      <Card className="border-slate-200">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead className="text-xs uppercase">Data</TableHead>
                <TableHead className="text-xs uppercase">Descrizione</TableHead>
                <TableHead className="text-xs uppercase">Conto</TableHead>
                <TableHead className="text-xs uppercase">Categoria</TableHead>
                <TableHead className="text-xs uppercase">Cantiere</TableHead>
                <TableHead className="text-xs uppercase text-right">Entrata</TableHead>
                <TableHead className="text-xs uppercase text-right">Uscita</TableHead>
                <TableHead className="text-xs uppercase text-right">Azioni</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {movimenti.length === 0 ? (
                <TableRow><TableCell colSpan={8} className="text-center text-slate-500 py-10">
                  <BookOpen className="h-10 w-10 mx-auto text-slate-300 mb-2" />
                  Nessun movimento — clicca "Entrata" o "Uscita" per iniziare
                </TableCell></TableRow>
              ) : movimenti.map(m => {
                const cm = contoMeta(m.conto);
                const isEntrata = m.tipo === 'entrata';
                return (
                  <TableRow key={m.id} className="border-slate-100" data-testid={`prima-nota-row-${m.id}`}>
                    <TableCell className="text-sm">{fmtDate(m.data, 'dd-MM-yyyy')}</TableCell>
                    <TableCell className="font-medium">{m.descrizione}</TableCell>
                    <TableCell><Badge variant="outline" className={`text-xs ${cm.color}`}>{cm.label}</Badge></TableCell>
                    <TableCell className="text-sm text-slate-600">{m.categoria || '-'}</TableCell>
                    <TableCell className="text-sm">{m.cantiere_name || '-'}</TableCell>
                    <TableCell className="text-right font-medium text-emerald-700">{isEntrata ? fmt(m.importo) : ''}</TableCell>
                    <TableCell className="text-right font-medium text-red-700">{!isEntrata ? fmt(m.importo) : ''}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(m)} data-testid={`edit-pn-${m.id}`}>
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                        <Button variant="ghost" size="icon" className="h-8 w-8 text-red-500 hover:text-red-700 hover:bg-red-50" onClick={() => handleDelete(m)} data-testid={`delete-pn-${m.id}`}>
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-[560px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {form.tipo === 'entrata' ? <ArrowUpRight className="h-5 w-5 text-emerald-600" /> : <ArrowDownRight className="h-5 w-5 text-red-600" />}
              {editingId ? 'Modifica' : 'Nuova'} {form.tipo === 'entrata' ? 'Entrata' : 'Uscita'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-3 mt-2">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label>Tipo *</Label>
                <Select value={form.tipo} onValueChange={v => setForm({...form, tipo: v})}>
                  <SelectTrigger data-testid="pn-tipo"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="entrata">Entrata (denaro in)</SelectItem>
                    <SelectItem value="uscita">Uscita (denaro out)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label>Data *</Label>
                <Input type="date" value={form.data} onChange={e => setForm({...form, data: e.target.value})} required data-testid="pn-data" />
              </div>
            </div>
            <div className="space-y-1">
              <Label>Descrizione *</Label>
              <Input value={form.descrizione} onChange={e => setForm({...form, descrizione: e.target.value})} required placeholder="Es. Pagamento fattura ACME, prelievo cassa..." data-testid="pn-descrizione" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label>Importo (€) *</Label>
                <Input type="number" step="0.01" value={form.importo} onChange={e => setForm({...form, importo: e.target.value})} required data-testid="pn-importo" />
              </div>
              <div className="space-y-1">
                <Label>Conto</Label>
                <Select value={form.conto} onValueChange={v => setForm({...form, conto: v})}>
                  <SelectTrigger data-testid="pn-conto"><SelectValue /></SelectTrigger>
                  <SelectContent>{CONTI.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label>Categoria</Label>
                <Select value={form.categoria || 'none'} onValueChange={v => setForm({...form, categoria: v === 'none' ? '' : v})}>
                  <SelectTrigger><SelectValue placeholder="Seleziona..." /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Nessuna</SelectItem>
                    {CATEGORIE.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label>Cantiere (opz.)</Label>
                <Select value={form.cantiere_id || 'none'} onValueChange={v => setForm({...form, cantiere_id: v === 'none' ? '' : v})}>
                  <SelectTrigger><SelectValue placeholder="Nessuno" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Nessuno</SelectItem>
                    {cantieri.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1">
              <Label>Note</Label>
              <Textarea rows={2} value={form.note} onChange={e => setForm({...form, note: e.target.value})} />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Annulla</Button>
              <Button type="submit" className="bg-slate-900 text-white" data-testid="submit-pn">{editingId ? 'Salva' : 'Registra'}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
